// CommunityDiscover — MVP public showcase page (F8.2).
// Placeholder data for groups + events. Real integration with Community API
// deferred to a later phase.
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, Users, Calendar } from "lucide-react";

// @deprecated F12.1 — HeaderV2 + FooterPremiumDark are rollback-only now
//             (REACT_APP_HEADER_V4_ENABLED=false). HeaderV4 + FooterV4 are
//             the default public chrome.
import HeaderV2 from "@/pages/landing-v2/components/HeaderV2";
import FooterPremiumDark from "@/pages/landing-v2/sections/FooterPremiumDark";
import HeaderV4 from "@/components/header-v4/HeaderV4";
import FooterV4 from "@/components/footer-v4/FooterV4";
import CTAButton from "@/pages/landing-v2/components/CTAButton";
import useAuthCTA from "@/hooks/useAuthCTA";

const V4_ENABLED = (process.env.REACT_APP_HEADER_V4_ENABLED ?? "true") !== "false";

const GROUP_KEYS = [
    { key: "solopreneurs", mockMembers: 2847, emoji: "🚀" },
    { key: "saas_builders", mockMembers: 4123, emoji: "⚙️" },
    { key: "ai_automators", mockMembers: 5981, emoji: "🤖" },
];

const EVENT_KEYS = ["e1", "e2", "e3"];

export default function CommunityDiscover() {
    const { t } = useTranslation("landing_v2");
    const authCTA = useAuthCTA();

    useEffect(() => {
        const prev = document.body.style.backgroundColor;
        document.body.style.backgroundColor = "var(--color-bg)";
        return () => { document.body.style.backgroundColor = prev; };
    }, []);

    return (
        <div
            className="min-h-screen bg-bg text-text-main font-sans antialiased selection:bg-brand-primary/30"
            data-testid="community-discover-root"
        >
            {V4_ENABLED ? <HeaderV4 /> : <HeaderV2 />}
            <main>
                {/* Hero */}
                <section className="relative overflow-hidden pt-32 lg:pt-40 pb-16 lg:pb-24">
                    <div className="absolute inset-0 pointer-events-none" aria-hidden>
                        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-25 blur-3xl bg-[radial-gradient(closest-side,_#FFFFFF_0%,_transparent_70%)]" />
                    </div>
                    <div className="relative max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                            className="text-text-main font-bold tracking-[-0.03em] leading-[1.05] text-[clamp(40px,6vw,80px)] mb-6"
                        >
                            {t("community_discover.hero.title")}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-text-secondary text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl mx-auto"
                        >
                            {t("community_discover.hero.sub")}
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        >
                            <CTAButton
                                variant="primary"
                                onClick={() => authCTA.signup()}
                                icon={ArrowRight}
                                testId="community-discover-hero-cta"
                            >
                                {t("community_discover.hero.cta")}
                            </CTAButton>
                        </motion.div>
                    </div>
                </section>

                {/* Groups */}
                <section className="relative py-16 lg:py-20 border-t border-brand-border">
                    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="text-text-main text-[clamp(28px,3.5vw,44px)] font-bold tracking-[-0.02em] mb-10"
                        >
                            {t("community_discover.groups.title")}
                        </motion.h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            {GROUP_KEYS.map((g, i) => (
                                <motion.article
                                    key={g.key}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.5, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                    className="p-6 rounded-2xl bg-card-bg border border-brand-border hover:border-brand-primary/50 transition-colors"
                                    data-testid={`community-discover-group-${g.key}`}
                                >
                                    <div className="text-4xl mb-4" aria-hidden>{g.emoji}</div>
                                    <h3 className="text-text-main text-xl font-semibold tracking-tight mb-2">
                                        {t(`community_discover.groups.items.${g.key}.name`)}
                                    </h3>
                                    <div className="flex items-center gap-1.5 text-xs text-text-secondary mb-4">
                                        <Users className="w-3.5 h-3.5" aria-hidden />
                                        {g.mockMembers.toLocaleString()} {t("community_discover.groups.members_label")}
                                    </div>
                                    <p className="text-text-secondary text-sm leading-relaxed mb-5">
                                        {t(`community_discover.groups.items.${g.key}.description`)}
                                    </p>
                                    <CTAButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => authCTA.signup()}
                                        className="w-full"
                                    >
                                        {t("community_discover.groups.join_cta")}
                                    </CTAButton>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Events */}
                <section className="relative py-16 lg:py-24 border-t border-brand-border">
                    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
                        <motion.h2
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                            className="text-text-main text-[clamp(28px,3.5vw,44px)] font-bold tracking-[-0.02em] mb-10"
                        >
                            {t("community_discover.events.title")}
                        </motion.h2>
                        <div className="space-y-4">
                            {EVENT_KEYS.map((k, i) => (
                                <motion.article
                                    key={k}
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                                    className="p-5 lg:p-6 rounded-xl bg-card-bg border border-brand-border hover:border-brand-primary/40 transition-colors flex flex-col md:flex-row md:items-center gap-4"
                                    data-testid={`community-discover-event-${k}`}
                                >
                                    <div className="shrink-0 flex items-center gap-3 md:w-56">
                                        <Calendar className="w-5 h-5 text-brand-primary shrink-0" aria-hidden />
                                        <span className="text-text-main font-mono text-sm">
                                            {t(`community_discover.events.items.${k}.date`)}
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-text-main text-base lg:text-lg font-semibold leading-snug mb-1">
                                            {t(`community_discover.events.items.${k}.title`)}
                                        </h3>
                                        <p className="text-text-secondary text-xs lg:text-sm">
                                            {t(`community_discover.events.items.${k}.speaker`)}
                                        </p>
                                    </div>
                                    <div className="shrink-0">
                                        <CTAButton variant="ghost" size="sm" onClick={() => authCTA.signup()}>
                                            {t("community_discover.events.rsvp_cta")}
                                        </CTAButton>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                </section>
            </main>
            {V4_ENABLED ? <FooterV4 /> : <FooterPremiumDark />}
        </div>
    );
}
